import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import AssignmentChip from '@/components/schedule/AssignmentChip';
import {
  buildMonthGrid,
  groupByDate,
  isoDate,
  startOfMonth,
  sameIsoDate,
} from '@/lib/scheduleData';

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const CalendarMonth = ({
  cursor,
  assignments = [],
  jobs = [],
  onPickDate,
  onPickAssignment,
}) => {
  const { t } = useTranslation();
  const days = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const groups = useMemo(() => groupByDate(assignments), [assignments]);
  const monthStart = startOfMonth(cursor);
  const today = new Date();

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-3 sm:p-4">
        {/* Weekday header */}
        <div
          className="grid grid-cols-7 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border pb-2 mb-2"
          data-testid="calendar-weekdays"
        >
          {WEEKDAY_KEYS.map((key) => (
            <div key={key} className="px-2 py-1">
              {t(`schedule.weekdays.${key}`)}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-px bg-border rounded-md overflow-hidden" data-testid="calendar-month-grid">
          {days.map((d) => {
            const inMonth = d.getMonth() === monthStart.getMonth();
            const isToday = sameIsoDate(d, today);
            const key = isoDate(d);
            const list = groups.get(key) || [];
            return (
              <div
                role="button"
                tabIndex={0}
                key={key}
                onClick={() => onPickDate?.(d)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPickDate?.(d);
                  }
                }}
                data-testid={`calendar-day-${key}`}
                data-today={isToday ? 'true' : undefined}
                data-in-month={inMonth ? 'true' : 'false'}
                className={cn(
                  'relative text-left cursor-pointer bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))]/40 transition-colors min-h-[88px] sm:min-h-[110px] p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40',
                  !inMonth && 'opacity-50',
                  isToday && 'ring-1 ring-[hsl(var(--primary))]/60'
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'text-[11px] font-semibold tabular-nums',
                      isToday ? 'text-[hsl(var(--primary))]' : 'text-foreground'
                    )}
                  >
                    {d.getDate()}
                  </span>
                  {isToday ? (
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                      {t('schedule.todayBadge')}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1.5 space-y-1">
                  {list.slice(0, 3).map((a) => (
                    <AssignmentChip
                      key={a.id}
                      assignment={a}
                      jobs={jobs}
                      onClick={onPickAssignment}
                      compact
                    />
                  ))}
                  {list.length > 3 ? (
                    <div className="text-[10px] text-muted-foreground px-1">
                      {t('schedule.moreCount', { count: list.length - 3 })}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarMonth;
